import React, { useEffect } from "react";
import {
  beautify,
  createColumn,
  createViewEdit,
  formatAmount,
  getForm,
  getRegister,
  numberFormat,
} from "../../utils/helpers";
import { FormActions } from "../../components/common/FormActions";
import { URL_USSD_STICKERS } from "../../utils/urls";
import { API_DISTRICTS, API_USSD_STICKERS } from "../../utils/apis";
import useGet from "../common/useGet";
import { IFormSection, TFormHookParams } from "../../types/common";
import { BooleanTags } from "../../components/common/registerUtils";

export const useUSSDStickers = () => {
  const tableColumns = () => [
    ...[
      "ussd_sticker_name",
      "ussd_sticker_amount",
      "city_name",
      "is_active",
      "created_by",
      "actions",
    ].map((item) =>
      createColumn(
        item,
        item === "actions" || item === "amount" ? false : true,
        beautify(item),
      ),
    ),
  ];

  const tableData = (data: any) => {
    if (!data) return [];

    return data.map((item: any) => ({
      ...item,
      ussd_sticker_amount: numberFormat(item?.ussd_sticker_amount),
      is_active: <BooleanTags status={item.is_active} />,
      created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
      actions: (
        <FormActions
          actions={[...createViewEdit(URL_USSD_STICKERS, item?.id)]}
        />
      ),
    }));
  };

  const viewData = (item) => {
    if (!item) {
      return {
        sections: [
          {
            title: "Error",
            fields: { message: "No data available" },
          },
        ],
      };
    }

    return {
      sections: [
        {
          title: "USSD log Details",
          fields: {
            ...item,
            id: item?.id,
            ussd_sticker_amount: formatAmount(item.ussd_sticker_amount),
            is_active: <BooleanTags status={item.is_active} />,
            created_by: `${item?.created_by?.first_name} ${item?.created_by?.last_name}`,
          },
        },
      ],
    };
  };

  const formRoute = getForm(URL_USSD_STICKERS);

  return { tableColumns, tableData, viewData, formRoute };
};

export const useUSSDStickersForm = ({ id }: TFormHookParams = {}) => {
  const { loadData, data: districts, loading } = useGet();

  useEffect(() => {
    loadData({ api: API_DISTRICTS });
  }, []);

  const isEdit = !!id;

  const sections: IFormSection[] = [
    {
      title: "USSD Sticker Details",
      fields: [
        {
          field_name: "ussd_sticker_name",
          label: "USSD Sticker Name",
          type: "text",
          props: {
            required: !isEdit,
            placeholder: "Enter ussd sticker name",
          },
        },
        {
          field_name: "ussd_sticker_amount",
          label: "USSD Sticker Amount",
          type: "number",
          props: {
            required: !isEdit,
            placeholder: "Enter ussd sticker amount",
          },
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          props: { required: !isEdit },
          options_data: districts,
        },
        {
          field_name: "is_active",
          label: "Is Active",
          type: "select",
          props: { required: !isEdit },
          options_data: [
            { id: true, name: "True" },
            { id: false, name: "False" },
          ],
        },
      ],
    },
  ];

  const title = "USSD Sticker";
  const redirect = getRegister(URL_USSD_STICKERS);

  const onSubmit = (payload: any, id: string) => {
    if (id) {
      return {
        id,
        api: API_USSD_STICKERS,
        params: {
          ...payload,
        },
      };
    }
    return {
      api: API_USSD_STICKERS,
      params: {
        ...payload,
      },
    };
  };

  return {
    sections,
    title,
    redirect,
    loading,
    onSubmit,
  };
};
